import { useState, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBuildInfo } from '../../buildInfo';
import { getEmulatorHost, isUsingEmulators } from '../../services/emulator';

function getFriendlyLoginError(err: any, t: (key: string) => string): string {
  const code = String(err?.code || '');

  if (isUsingEmulators()) {
    const emulatorHost = getEmulatorHost();
    const uiUrl = `http://${emulatorHost}:4000`;

    if (code === 'auth/user-not-found') {
      return (
        'No user found in the Auth emulator for that email. ' +
        'Local emulators have their own separate user database, so you need to Sign up first. ' +
        `If you have the Emulator UI running, you can also inspect users at ${uiUrl}.`
      );
    }

    if (code === 'auth/wrong-password') {
      return 'Wrong password for that emulator user. Try again, or Sign up to create a new local user.';
    }

    if (code === 'auth/invalid-email') {
      return 'Invalid email format.';
    }
  }

  if (code === 'auth/user-not-found' || code === 'auth/wrong-password') {
    return t('auth.invalidCredentials');
  }

  return t('auth.failedLogin');
}

export function Login() {
  const build = getBuildInfo();
  const { t } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    console.log('✅ Login form submitted');

    if (!email || !password) {
      setError(t('auth.emailPasswordRequired'));
      return;
    }

    try {
      setError('');
      setLoading(true);
      console.log('📝 Attempting login...');
      await signIn(email, password);
      console.log('✅ Login completed, navigating to dashboard');
      navigate('/dashboard');
      console.log('✅ Navigation triggered');
    } catch (err: any) {
      console.error('❌ Login error:', err.message || err);
      setError(getFriendlyLoginError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('🔘 Google button clicked');
    try {
      setError('');
      setLoading(true);
      console.log('🔘 Calling signInWithGoogle...');
      await signInWithGoogle();
      console.log('🔘 signInWithGoogle returned, navigating...');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('🔘 Google sign-in failed:', err.code, err.message, err);
      setError(`${t('auth.failedGoogleSignIn')}: ${err.code || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: 'transparent',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: 'var(--surface)',
        padding: '40px',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
        width: '100%',
        maxWidth: '400px',
      }}>
        <h2 style={{ marginBottom: '24px', textAlign: 'center' }}>
          {t('auth.welcomeBack')}{' '}
          <span style={{ display: 'block', marginTop: '6px', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 400 }}>
            v{build.version} · {build.branch}{build.sha ? `@${build.sha}` : ''}{build.buildTime ? ` · ${build.buildTime}` : ''}
          </span>
        </h2>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: 'var(--danger-soft)',
            color: 'var(--danger)',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              {t('auth.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '16px',
              }}
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '16px',
              }}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: loading ? 'var(--border)' : 'var(--primary)',
              color: loading ? 'var(--text-muted)' : 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('auth.loggingIn') : t('auth.logIn')}
          </button>
        </form>

        <div style={{
          marginTop: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border)' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{t('common.or')}</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border)' }} />
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            marginTop: '20px',
            backgroundColor: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
          {t('auth.continueWithGoogle')}
        </button>

        <p style={{ marginTop: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
          {t('auth.noAccount')}{' '}
          <a href="/signup" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            {t('auth.signUpLink')}
          </a>
        </p>
      </div>
    </div>
  );
}
